import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Icon,
  Switch,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import ImportResultSummary from './ImportResultSummary';

// Step 3 of the import flow: preview parsed rows with per-row validation
// status, a valid/error summary, and the commit action.
const ImportPreviewTable = ({
  parsing,
  rows,
  entityConfig,
  importing,
  importResult,
  onImport,
  onReset,
}) => {
  const [errorsOnly, setErrorsOnly] = useState(false);

  const validRows = rows.filter((r) => r.errors.length === 0);
  const invalidRows = rows.filter((r) => r.errors.length > 0);

  const visibleRows = useMemo(
    () => rows
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => !errorsOnly || row.errors.length > 0),
    [rows, errorsOnly],
  );

  // With MUI's stickyHeader, the header row is positioned independently of the
  // scrolling body — if the <Table> is left at its default width: 100%, the
  // browser can't fit column widths that add up to more than the visible
  // container, and silently divides the body's columns equally instead of
  // honoring the widths below (while the header keeps sizing itself from
  // content, so the two rows visibly diverge). Giving the table an explicit
  // total width — the sum of every column below — guarantees table-layout:
  // fixed has room to honor each one, and TableContainer's horizontal scroll
  // takes over for the rest, exactly like a spreadsheet.
  const tableWidth = 44 + 140 + (entityConfig?.columns.reduce((sum, c) => sum + (c.width || 160), 0) ?? 0);

  return (
    <>
      <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <MDTypography variant="h6">Step 3 &mdash; Preview &amp; Import</MDTypography>
        {rows.length > 0 && (
          <MDBox display="flex" gap={1}>
            <Chip label={`${validRows.length} valid`} color="success" size="small" />
            {invalidRows.length > 0 && (
              <Chip label={`${invalidRows.length} with errors`} color="error" size="small" />
            )}
          </MDBox>
        )}
      </MDBox>

      {parsing && (
        <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
          <Icon sx={{ fontSize: 32, color: 'info.main' }}>hourglass_top</Icon>
          <MDTypography variant="body2" color="text" mt={1}>
            Reading your file&hellip;
          </MDTypography>
        </MDBox>
      )}

      {!parsing && rows.length === 0 && (
        <MDBox display="flex" flexDirection="column" alignItems="center" py={6}>
          <Icon sx={{ fontSize: 40, color: 'text.disabled' }}>table_chart</Icon>
          <MDTypography variant="body2" color="text" mt={1}>
            Upload a file in Step 2 to see a preview here
          </MDTypography>
        </MDBox>
      )}

      {!parsing && rows.length > 0 && entityConfig && (
        <>
          {invalidRows.length > 0 && (
            <MDBox display="flex" alignItems="center" justifyContent="flex-end" mb={1}>
              <MDTypography variant="caption" color="text" mr={0.5}>
                Show only rows with errors
              </MDTypography>
              <Switch
                size="small"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
              />
            </MDBox>
          )}

          <TableContainer sx={{ maxHeight: 420, mb: 2 }}>
            {/* Material Dashboard's global theme (assets/theme/components/table/
                tableHead.js) sets MuiTableHead to display: block app-wide, styled
                for its own card-style table headers. That's harmless for tables
                whose columns aren't individually width-constrained, but it takes
                <thead> out of the table's row-group/column model entirely — its
                <tr> then lays out as its own disconnected mini-table, so the
                header sizes columns purely from content while the real <tbody>
                (still a proper table-row-group) divides the table's declared
                width equally instead of honoring each column's width below. This
                is a real, wide-reaching theme default, not something to change
                globally — so it's reset back to table-header-group just for this
                data-grid-style table. */}
            <Table size="small" sx={{ tableLayout: 'fixed', width: tableWidth, minWidth: tableWidth }}>
              <TableHead sx={{ display: 'table-header-group', p: 0, borderRadius: 0 }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 44 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 140 }}>Status</TableCell>
                  {entityConfig.columns.map((col) => (
                    <TableCell key={col.key} sx={{ fontWeight: 700, width: col.width || 160, whiteSpace: 'nowrap' }}>
                      {col.label}
                      {col.required && (
                        <MDTypography component="span" variant="caption" color="error" ml={0.25}>
                          *
                        </MDTypography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map(({ row, idx }) => (
                  <TableRow
                    key={idx}
                    sx={row.errors.length > 0 ? { bgcolor: 'rgba(239,68,68,0.06)' } : undefined}
                  >
                    <TableCell sx={{ width: 44, verticalAlign: 'top' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ width: 140, verticalAlign: 'top' }}>
                      {row.errors.length === 0 ? (
                        <Chip label="Valid" color="success" size="small" variant="outlined" />
                      ) : (
                        <MDBox>
                          <Chip label="Error" color="error" size="small" variant="outlined" />
                          <MDBox mt={0.5}>
                            {row.errors.map((msg) => (
                              <MDTypography key={msg} variant="caption" color="error" display="block" sx={{ whiteSpace: 'normal' }}>
                                {msg}
                              </MDTypography>
                            ))}
                          </MDBox>
                        </MDBox>
                      )}
                    </TableCell>
                    {entityConfig.columns.map((col) => {
                      const value = String(row.data[col.label] ?? '');
                      return (
                        <TableCell
                          key={col.key}
                          title={value}
                          sx={{ width: col.width || 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <ImportResultSummary importResult={importResult} onReset={onReset} />

          {!importResult?.success && (
            <>
              <MDButton
                variant="gradient"
                color="info"
                disabled={validRows.length === 0 || importing}
                onClick={onImport}
                startIcon={<Icon>{importing ? 'hourglass_top' : 'save'}</Icon>}
              >
                {importing ? 'Importing…' : `Import ${validRows.length} Valid Row${validRows.length === 1 ? '' : 's'}`}
              </MDButton>
              {invalidRows.length > 0 && (
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Rows with errors are skipped and won&rsquo;t be imported. Fix them in your file and re-upload if needed.
                </MDTypography>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

ImportPreviewTable.propTypes = {
  parsing: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.object.isRequired,
      errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  entityConfig: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        width: PropTypes.number,
      }),
    ),
  }),
  importing: PropTypes.bool.isRequired,
  importResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    created: PropTypes.number,
    updated: PropTypes.number,
    skipped: PropTypes.number,
    rowErrors: PropTypes.arrayOf(
      PropTypes.shape({
        row: PropTypes.number,
        message: PropTypes.string,
      }),
    ),
  }),
  onImport: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

ImportPreviewTable.defaultProps = {
  entityConfig: null,
  importResult: null,
};

export default ImportPreviewTable;
